import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { NoSymbolIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

// Module-private sentinel for the inline-create row. Using a Symbol prevents
// any collision with a real option whose value happens to be a string like
// '__CREATE__'. Option values are passed through Headless UI's Combobox by
// reference, which preserves Symbol identity.
const CREATE_SENTINEL = Symbol('typeahead-create');

/**
 * TypeaheadChips — multi-select with typeahead search and dismissible chips
 * (LAT-12). Used for every relational/multi field: salesperson(s), crew
 * members, techs, accessories, lead sources, etc.
 *
 * ── Chip identity treatment (canonical, operator ruling 2026-07-25) ──────
 *   Red pill = a user (admin, sales, office, tech) OR a generic tag
 *              (resource, document, quote-item type).
 *   Neutral pill + coloured swatch = a crew (or any future identity chip
 *              whose identity is a stored entity).
 *
 *   The identity treatment must be neutral-pill + coloured swatch ALWAYS —
 *   the swatch carries the identity colour, the pill stays neutral. A crew
 *   whose colour is red must still read as a crew chip (neutral pill, red
 *   swatch), NOT as a generic red chip.
 *
 *   Callers select treatment via the explicit `variant` prop below. Do NOT
 *   infer treatment from data shape (e.g., "has color?") — that is what
 *   produced the FP3 crew-chip identity bug.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Pattern:
 *   1. User types in the input → options filter live
 *   2. User picks an option → it becomes a dismissible chip
 *   3. Optional: when no options match, show "+ Add «typed text»" inline
 *
 * Props:
 *   value          string[]                    selected option values
 *   onChange(arr)                              new array of selected values
 *   options        [{ value, label, hint?, color? }, …]   flat list.
 *                    color?: string — optional per-option identity color
 *                    (e.g., crew swatch). Only consulted when
 *                    variant === 'identity'; ignored for 'generic'.
 *                    chipOnly?: bool — IDI-681: resolves a selected chip's
 *                    label but is never listed in the dropdown (e.g. a
 *                    deactivated person kept on a record by saved name).
 *                    deactivated?: bool — IDI-681: the chip shows a
 *                    circle-slash icon after its (truncated) label, with
 *                    title and accessible name "Deactivated". Chip-only;
 *                    never part of the label.
 *   variant        'generic' | 'identity'  (default 'generic')
 *                    'generic'  → red pill, ignores option.color entirely,
 *                                 no swatch. Techs, resources, quote items,
 *                                 documents.
 *                    'identity' → neutral pill + coloured swatch driven by
 *                                 option.color. If option.color is missing,
 *                                 renders a neutral-400 placeholder swatch
 *                                 so a data bug is visible, not silent.
 *   placeholder    string
 *   allowCreate    bool — show "+ Add «text»" when no exact match
 *   onCreate(str)  fn — called when user picks the inline-create row;
 *                  parent decides what to do (push into options + value)
 *   max            number — cap the count
 *   emptyMessage   string — copy shown in the panel when no options match.
 *                  Consumers pass a two-state message (empty pool vs. filter
 *                  miss) so the atom stays dumb about the distinction.
 *   id, name, disabled, invalid, className
 *
 * ── D-11 (PR-72 Commit 2) — open-on-click before any keystroke ───────────
 *   Stakeholder rule: type-ahead fields must open their list on click,
 *   before the user types anything.
 *
 *   Fix location: HERE, on the primitive. TypeaheadChips has three real
 *   consumers (WOCreateSheets, WorkOrderDetail, TaxRatesPage); fixing the
 *   rule at each call site would turn one global rule into three local
 *   ones — exactly the failure mode the operator flagged. See
 *   `TypeaheadChips.d11-open-on-click.test.js` for the red-proof.
 *
 *   Mechanism (v2): a HIDDEN <Combobox.Button> (sr-only, tabIndex=-1,
 *   aria-hidden) is rendered inside the anchor. The wrapper's onClick and
 *   the input's onFocus both invoke `openButtonRef.current?.click()` when
 *   the combobox is not already open. This is EXPLICIT open-on-interaction,
 *   NOT focus-event-triggered.
 *
 *   Why not the `immediate` prop alone: `immediate` opens on the focus
 *   EVENT, not the focus STATE. After Escape dismissal, focus stays on the
 *   input, so a second click's inputRef.current.focus() is a no-op and
 *   fires no focus event — the panel would stay shut (dead click, worse
 *   than the original bug). Verified reproducible by the
 *   `re-opens on a second click after Escape` test.
 *
 *   Dispatch 409 addendum — double-fire on the anchor padding band:
 *   The anchor onClick MUST sample focus state BEFORE it calls .focus().
 *   Without the sample, a user click landing on the anchor'"'"'s padding
 *   band (input still unfocused) causes a double-fire: line 1 (.focus())
 *   dispatches onFocus synchronously, onFocus force-opens via the hidden
 *   trigger, control returns to line 2, `open` in the outer render-prop
 *   closure is STILL false (React batches discrete-event updates at the
 *   outermost dispatch), the guard passes, and the trigger fires a second
 *   time — toggling the panel closed. Made mutually exclusive by
 *   construction: onClick opens ONLY when the input was already focused
 *   (i.e. onFocus won'"'"'t fire and we'"'"'re the sole path). State-only tests
 *   cannot distinguish "never fired" from "fired twice," so the red-proof
 *   suite also counts trigger clicks directly.
 *
 *   Sibling primitive note: `calendar/LocationCombobox.js` satisfies the
 *   same rule via a VISIBLE <ComboboxButton> (v2 flat imports). This
 *   primitive uses a HIDDEN Combobox.Button driven programmatically.
 *   Same underlying mechanism (button-triggered open), different visual
 *   presentation — dictated by the anchor's contents. TypeaheadChips has
 *   dismissible chips inside its anchor, incompatible with a visible
 *   trigger in the anchor slot; LocationCombobox's anchor is a plain
 *   input where a visible chevron button is natural. Do not "harmonise"
 *   without preserving both.
 * ────────────────────────────────────────────────────────────────────────
 */
export default function TypeaheadChips({
  value = [],
  onChange,
  options = [],
  placeholder = 'Search…',
  allowCreate = false,
  onCreate,
  max,
  disabled = false,
  invalid = false,
  emptyMessage = 'No matches',
  variant = 'generic',
  id,
  name,
  className,
  'aria-label': ariaLabel,
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  // D-11 (PR-72 Commit 2): hidden Combobox.Button ref for explicit
  // open-on-interaction. See docblock and the anchor onClick below.
  const openButtonRef = useRef(null);
  const [flipUp, setFlipUp] = useState(false);
  const [panelMaxHeight, setPanelMaxHeight] = useState(null);

  // PT-22 W3 FP3-1 F5: dynamic flip decision.
  //
  // Prior behaviour: fixed 160px viewport-bottom threshold + no offset. That
  // occluded the trigger on flip-up whenever the panel was taller than the
  // space between the trigger and the previous sibling (operator hit this
  // on the Assign section's `Crew | Individual tech(s)` toggle at the WO
  // create-sheet).
  //
  // New behaviour:
  //   1. Measure the panel's actual height via `panelRef` (mounted only
  //      while open). Fall back to `PANEL_MAX_HEIGHT_PX` (matches Tailwind
  //      `max-h-72` = 288px) before the first measurement.
  //   2. Compute space above/below the trigger from its rect.
  //   3. Flip up ONLY when the panel doesn't fit below AND fits better above.
  //   4. Cap the panel's `max-height` to whichever side is chosen, minus a
  //      small viewport-edge margin, so the trigger + any label directly
  //      above it remain visible.
  //   5. Re-run on focus, window resize, ancestor scroll, AND whenever
  //      `filtered` or `value` changes (panel height can change with filter
  //      hits or after a chip is added/removed).
  //
  // DashboardJobsTable's separate 160px threshold does NOT move in lockstep
  // (operator ruling 2026-07-25 — diverge, each surface tunes independently).
  const PANEL_MAX_HEIGHT_PX = 288; // Tailwind max-h-72
  const VIEWPORT_EDGE_MARGIN_PX = 8;

  const checkFlip = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // Measured panel height if mounted, else assume the fallback max.
    const measured =
      panelRef.current?.getBoundingClientRect().height ?? PANEL_MAX_HEIGHT_PX;
    const panelH = Math.min(measured, PANEL_MAX_HEIGHT_PX);

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_EDGE_MARGIN_PX;
    const spaceAbove = rect.top - VIEWPORT_EDGE_MARGIN_PX;

    const fitsBelow = spaceBelow >= panelH;
    const shouldFlip = !fitsBelow && spaceAbove > spaceBelow;
    setFlipUp(shouldFlip);

    // Cap the panel so it never overflows the chosen side. This keeps the
    // trigger (and, on flip-up, any label directly above it) visible.
    const cap = shouldFlip
      ? Math.max(80, Math.floor(spaceAbove))
      : Math.max(80, Math.floor(spaceBelow));
    setPanelMaxHeight(Math.min(cap, PANEL_MAX_HEIGHT_PX));
  };

  // Recompute flip direction on window resize + on any scroll inside an
  // ancestor (e.g., the slideover body scrolls). The 'capture' phase catches
  // scrolls on nested scroll containers.
  useEffect(() => {
    const handler = () => checkFlip();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const valueSet = useMemo(() => new Set(value), [value]);
  const optionMap = useMemo(() => {
    const m = new Map();
    for (const o of options) m.set(o.value, o);
    return m;
  }, [options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => {
      if (o.chipOnly) return false; // IDI-681: chip label only, never offered
      if (valueSet.has(o.value)) return false; // hide already-selected
      if (!q) return true;
      return o.label.toLowerCase().includes(q);
    });
  }, [options, query, valueSet]);

  // F5 DoD item 2: re-run flip decision when the panel's content changes
  // (filter hits change panel height; adding/removing chips changes the
  // trigger's rect). rAF defers until layout has settled.
  useEffect(() => {
    const id = requestAnimationFrame(() => checkFlip());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, value.length]);

  // Show "+ Add «text»" row when allowCreate, query has content,
  // and no option matches the typed text exactly.
  const exactMatch = filtered.some(
    (o) => o.label.toLowerCase() === query.trim().toLowerCase()
  );
  const showCreate =
    allowCreate &&
    !disabled &&
    !!query.trim() &&
    !exactMatch &&
    !options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase());

  const atMax = typeof max === 'number' && value.length >= max;

  function removeChip(v) {
    onChange?.(value.filter((x) => x !== v));
  }

  function handlePicked(picked) {
    if (picked == null) return;
    if (picked === CREATE_SENTINEL) {
      const created = query.trim();
      if (!created) return;
      onCreate?.(created);     // parent handles options+value update
      setQuery('');
      return;
    }
    if (atMax) return;
    if (valueSet.has(picked)) return;
    onChange?.([...value, picked]);
    setQuery('');
  }

  function handleKeyDown(e) {
    // Backspace on empty input removes the last chip — friendly UX
    if (e.key === 'Backspace' && !query && value.length > 0) {
      e.preventDefault();
      removeChip(value[value.length - 1]);
    }
  }

  return (
    // D-11 (PR-72 Commit 2): open-on-interaction via a hidden
    // Combobox.Button (see docblock). The render-prop form gives us `open`
    // so anchor onClick can decide whether to open (closed) or leave
    // alone (already open) rather than blindly toggling.
    <Combobox value={null} onChange={handlePicked} disabled={disabled} nullable>
      {({ open }) => (
      <div ref={anchorRef} className={clsx('relative', className)}>
        {/* D-11: hidden trigger. Clicking it toggles the panel; we drive
            it programmatically from the anchor onClick and input onFocus.
            sr-only keeps it invisible; tabIndex=-1 keeps it out of the tab
            order; aria-hidden prevents screen-reader announcement. */}
        <Combobox.Button
          ref={openButtonRef}
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          data-testid="typeahead-open-button"
        />
        <div
          className={clsx(
            'flex flex-wrap items-center gap-1.5 px-2 py-1.5 transition-colors',
            'focus-within:ring-2 focus-within:ring-[color:var(--color-action-primary)]',
            disabled && 'opacity-60 cursor-not-allowed'
          )}
          style={{
            minHeight: 'var(--input-h)',
            borderRadius: 'var(--radius-md)',
            border: `1px solid ${
              invalid ? 'var(--color-error)' : 'var(--color-border)'
            }`,
            backgroundColor: 'var(--color-surface)',
          }}
          data-testid="typeahead-anchor"
          onClick={() => {
            const wasFocused = document.activeElement === inputRef.current;
            inputRef.current?.focus();
            // dispatch 409 fix: sample focus BEFORE .focus() moves it.
            // If the input was already focused, `focus()` is a no-op and
            // no onFocus fires — we must force-open here to satisfy the
            // dead-click-after-Escape case (test 5). If it was NOT focused,
            // .focus() dispatches onFocus synchronously, which will open
            // the panel; firing here too would be a second click on the
            // hidden trigger and toggle it closed. Mutually exclusive by
            // construction: exactly one of these branches opens per user
            // click. See docblock "dispatch 409" section.
            if (wasFocused && !open && !disabled) {
              openButtonRef.current?.click();
            }
          }}
        >
          {value.map((v) => {
            const opt = optionMap.get(v);
            return (
              <Chip
                key={v}
                label={opt?.chipLabel ?? opt?.label ?? v}
                title={opt?.title}
                color={opt?.color}
                deactivated={!!opt?.deactivated}
                variant={variant}
                onRemove={disabled ? null : () => removeChip(v)}
              />
            );
          })}
          <Combobox.Input
            ref={inputRef}
            id={id}
            name={name}
            aria-label={ariaLabel || placeholder}
            className="flex-1 min-w-[8ch] text-sm bg-transparent border-0 focus:outline-none focus:ring-0 p-1"
            style={{ color: 'var(--color-text-default)' }}
            placeholder={value.length === 0 ? placeholder : ''}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              checkFlip();
              // D-11: keyboard-tab-in also opens the panel before any
              // keystroke. Focus doesn't toggle (unlike click), so we can
              // call this unconditionally guarded on `open`.
              if (!open && !disabled) openButtonRef.current?.click();
            }}
            onKeyDown={handleKeyDown}
            displayValue={() => query}
            disabled={disabled || atMax}
            autoComplete="off"
          />
        </div>

        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Combobox.Options
            ref={panelRef}
            data-testid="typeahead-panel"
            data-flip={flipUp ? 'up' : 'down'}
            className={clsx(
              // FP3-1 F5: `max-h-72` retained as an upper bound; dynamic
              // `maxHeight` in style (below) caps it further when the
              // available side is smaller — keeps the trigger + any label
              // directly above it visible on flip-up.
              'absolute z-40 max-h-72 w-full overflow-auto py-1 text-sm shadow-lg focus:outline-none',
              flipUp ? 'bottom-full mb-1' : 'mt-1'
            )}
            style={{
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border-subtle)',
              boxShadow: 'var(--shadow-lg)',
              ...(panelMaxHeight != null ? { maxHeight: `${panelMaxHeight}px` } : null),
            }}
          >
            {filtered.length === 0 && !showCreate && (
              <div
                className="px-3 py-2 text-sm"
                style={{ color: 'var(--color-text-muted)' }}
              >
                {emptyMessage}
              </div>
            )}

            {filtered.map((opt) => (
              <Combobox.Option
                key={opt.value}
                value={opt.value}
                disabled={opt.disabled || atMax}
                className={({ active, disabled }) =>
                  clsx(
                    'relative cursor-pointer select-none py-2 pl-8 pr-3',
                    active && 'bg-[color:var(--neutral-50)]',
                    disabled && 'opacity-50 cursor-not-allowed'
                  )
                }
              >
                {({ active }) => (
                  <>
                    <span className="block truncate" style={{ color: 'var(--color-text-default)' }}>
                      {opt.label}
                    </span>
                    {opt.hint && (
                      <span
                        className="block text-xs"
                        style={{ color: 'var(--color-text-muted)' }}
                      >
                        {opt.hint}
                      </span>
                    )}
                  </>
                )}
              </Combobox.Option>
            ))}

            {showCreate && (
              <Combobox.Option
                value={CREATE_SENTINEL}
                className={({ active }) =>
                  clsx(
                    'relative cursor-pointer select-none py-2 pl-8 pr-3 border-t',
                    active && 'bg-[color:var(--neutral-50)]'
                  )
                }
                style={{ borderColor: 'var(--color-border-subtle)' }}
              >
                <span
                  className="block truncate font-medium"
                  style={{ color: 'var(--color-action-primary)' }}
                >
                  + Add “{query.trim()}”
                </span>
              </Combobox.Option>
            )}
          </Combobox.Options>
        </Transition>

        {atMax && (
          <p
            className="mt-1 text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Maximum of {max} reached
          </p>
        )}
      </div>
      )}
    </Combobox>
  );
}

function Chip({ label, title, onRemove, color, deactivated = false, variant = 'generic' }) {
  // FP3-1 F1: variant is now EXPLICIT, not inferred from data shape.
  //   'generic'  → red pill, `color` ignored entirely, no swatch.
  //                Techs, resources, quote items, documents — a red pill
  //                signals "a user or a generic tag" per operator ruling
  //                (design-system note at top of file).
  //   'identity' → neutral pill + coloured swatch driven by `color`.
  //                Crews (and any future stored-entity identity chip).
  //                Missing `color` renders a visible neutral-400 placeholder
  //                so a data bug (crew record with no stored colour) is
  //                visible on the surface, not silently hidden.
  //
  // Prior FP2-F2 behaviour ("has color? → neutral palette") was data-shape
  // inference — exactly what produced the FP3 crew-chip identity bug where
  // a crew record missing `crew.color` fell through to `color: undefined`
  // and rendered as a solid red pill indistinguishable from a Resources chip.
  const isIdentity = variant === 'identity';
  const swatchColor = isIdentity
    ? (color || 'var(--neutral-400)')
    : null;
  const chipStyle = isIdentity
    ? {
        borderRadius: 'var(--radius-full)',
        backgroundColor: 'var(--neutral-50)',
        color: 'var(--color-text-default)',
        borderColor: 'var(--color-border)',
      }
    : {
        borderRadius: 'var(--radius-full)',
        backgroundColor: 'var(--red-50)',
        color: 'var(--red-700)',
        borderColor: 'var(--red-100)',
      };
  const removeBtnClass = isIdentity
    ? 'inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-[color:var(--neutral-100)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)]'
    : 'inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-[color:var(--red-100)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-action-primary)]';
  return (
    <span
      data-testid="typeahead-chip"
      data-variant={isIdentity ? 'identity' : 'generic'}
      data-swatch-color={isIdentity ? swatchColor : undefined}
      title={title}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border"
      style={chipStyle}
    >
      {isIdentity && (
        <span
          data-testid="typeahead-chip-swatch"
          className="inline-block h-2 w-2 rounded-full shrink-0"
          style={{ backgroundColor: swatchColor }}
          aria-hidden="true"
        />
      )}
      <span className="max-w-[16ch] truncate">{label}</span>
      {deactivated && (
        // IDI-681: outside the truncation so it is never clipped; native
        // title is the hover text (the app's tooltip pattern).
        <span
          role="img"
          aria-label="Deactivated"
          title="Deactivated"
          data-testid="typeahead-chip-deactivated"
          className="inline-flex shrink-0"
        >
          <NoSymbolIcon className="h-3 w-3" aria-hidden="true" />
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className={removeBtnClass}
          aria-label={`Remove ${label}`}
        >
          <XMarkIcon className="h-3 w-3" aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
